import { useQuery } from "@tanstack/react-query";

import { apiUrl } from "./apiClient";
import httpOption from "./httpOption";
import { readJsonOrThrow } from "./httpResponse";

async function vapiInterceptor(endpoint, method, bodyParams) {
  let fetchOptions;
  if (method === "GET") {
    fetchOptions = httpOption.GET();
  } else if (method === "POST") {
    fetchOptions = httpOption.POST();
    if (bodyParams) {
      fetchOptions.body = JSON.stringify(bodyParams);
    }
  } else {
    fetchOptions = { method };
    if (bodyParams) {
      fetchOptions.body = JSON.stringify(bodyParams);
    }
  }

  return fetch(apiUrl(`/vapi/${endpoint}`), fetchOptions);
}
export async function getAllModelNames(years, electrified = false) {
  let queryParams = [];

  if (years && years.length > 0) {
    queryParams.push(`years=${encodeURIComponent(years.join(","))}`);
  }

  if (electrified) {
    queryParams.push(`electrified=${electrified}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
  const endpoint = `getAllModelNames${queryString}`;

  const response = await vapiInterceptor(endpoint, "GET");
  return readJsonOrThrow(response, "Get model names");
}

export async function getVehicleAccessories(series, year) {
  const endpoint = `getAccessories?series=${encodeURIComponent(series)}&year=${encodeURIComponent(year)}`;
  const response = await vapiInterceptor(endpoint, "GET");
  return readJsonOrThrow(response, "Get vehicle accessories");
}

export async function getVehicleData(series, year) {
  const endpoint = `getVehicleData?series=${encodeURIComponent(series)}&year=${encodeURIComponent(year)}`;
  const response = await vapiInterceptor(endpoint, "GET");
  return readJsonOrThrow(response, "Get vehicle data");
}

export async function getElectrifiedComparisonData(series, year, trimCode) {
  const endpoint = `getElectrifiedComparisonData?series=${encodeURIComponent(series)}&year=${encodeURIComponent(year)}&trimCode=${encodeURIComponent(trimCode)}`;
  const response = await vapiInterceptor(endpoint, "GET");
  return readJsonOrThrow(response, "Get electrified comparison data");
}

export async function getVehicleColors(series, year) {
  const endpoint = `getVehicleColors?series=${encodeURIComponent(series)}&year=${encodeURIComponent(year)}`;
  const response = await vapiInterceptor(endpoint, "GET");
  return readJsonOrThrow(response, "Get vehicle colors");
}

export async function getVapiDisclaimers() {
  const endpoint = "disclaimers";
  const response = await vapiInterceptor(endpoint, "GET");
  const disclaimersResponse = await readJsonOrThrow(
    response,
    "Get VAPI disclaimers",
  );

  const disclaimers = disclaimersResponse.disclaimers.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.code]: curr.description,
    }),
    {},
  );

  return disclaimers;
}

export async function getModelYears(series, liveOnEngage = true) {
  const queryParams = [];

  if (series) {
    queryParams.push(`seriesId=${encodeURIComponent(series)}`);
  }

  if (liveOnEngage) {
    queryParams.push(`liveOnEngage=true`);
  }

  const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
  const endpoint = `getModelYears${queryString}`;

  const response = await vapiInterceptor(endpoint, "GET");
  return readJsonOrThrow(response, "Get model years");
}

export async function getLiveModelYears() {
  const response = await vapiInterceptor("getLiveModelYears", "GET");
  return readJsonOrThrow(response, "Get live model years");
}

// Custom hook to fetch VAPI disclaimers using React Query.
// This hook provides access to the disclaimers and handles caching and re-fetching.
export const useVapiDisclaimers = () => {
  return useQuery({
    queryKey: ["VapiDisclaimers"],
    queryFn: getVapiDisclaimers,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};
